import React, { FC } from "react";
import Avatar from "shared/Avatar/Avatar";
import { ChatCircle, Heart } from "@phosphor-icons/react";

export interface PostCardMetaProps {
  className?: string;
  meta: {
    date: string;
    author: { displayName: string; avatar: string };
    commentCount: number;
    like?: { count: number; isLiked?: boolean };
  };
  size?: "large" | "normal";
}

const PostCardMeta: FC<PostCardMetaProps> = ({
  className = "leading-none",
  meta,
  size = "normal",
}) => {
  const { date, author, commentCount, like } = meta;
  const likeCount = like?.count ?? 0;

  if (size === "large") {
    return (
      <div
        className={`nc-PostCardMeta flex flex-col gap-1 text-base ${className}`}
        data-nc-id="PostCardMeta"
      >
        <div className="inline-flex items-center gap-2">
          <Avatar
            radius="rounded-full"
            sizeClass="h-10 w-10 text-xl"
            imgUrl={author.avatar}
            userName={author.displayName}
          />
          <span className="text-neutral-700 dark:text-neutral-300 font-medium">
            {author.displayName}
          </span>
          <span className="text-neutral-300 dark:text-neutral-600">|</span>
          <span className="text-neutral-500 dark:text-neutral-400 font-normal">
            {date}
          </span>
        </div>
        {(!!commentCount || likeCount > 0) && (
          <div className="inline-flex items-center">
            <div className="w-10 mr-2 shrink-0" />
            <div className="inline-flex items-center gap-2">
              {!!commentCount && (
                <span className="inline-flex items-center gap-0.5 text-neutral-400 dark:text-neutral-500 text-base">
                  <ChatCircle size={17} weight="regular" />
                  {commentCount}
                </span>
              )}
              {likeCount > 0 && (
                <span className="inline-flex items-center gap-0.5 text-neutral-400 dark:text-neutral-500 text-base">
                  <Heart size={16} weight="regular" className="text-red-400" />
                  {likeCount}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`nc-PostCardMeta flex flex-col gap-1 text-sm ${className}`}
      data-nc-id="PostCardMeta"
    >
      <div className="inline-flex items-center gap-1.5">
        <Avatar
          radius="rounded-full"
          sizeClass="h-6 w-6 text-sm"
          imgUrl={author.avatar}
          userName={author.displayName}
        />
        <span className="text-neutral-600 dark:text-neutral-300 font-medium">
          {author.displayName}
        </span>
        <span className="text-neutral-300 dark:text-neutral-600">|</span>
        <span className="text-neutral-500 dark:text-neutral-400 font-normal">
          {date}
        </span>
      </div>
      {(!!commentCount || likeCount > 0) && (
        <div className="inline-flex items-center">
          <div className="w-6 mr-1.5 shrink-0" />
          <div className="inline-flex items-center gap-2">
            {!!commentCount && (
              <span className="inline-flex items-center gap-0.5 text-neutral-400 dark:text-neutral-500 text-sm">
                <ChatCircle size={15} weight="regular" />
                {commentCount}
              </span>
            )}
            {likeCount > 0 && (
              <span className="inline-flex items-center gap-0.5 text-neutral-400 dark:text-neutral-500 text-sm">
                <Heart size={14} weight="regular" className="text-red-400" />
                {likeCount}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PostCardMeta;
